// Which 3D demos exist, where their files are, and where each motion comes from (source and license).
// Files live in public/demos/ (same origin, no third-party request); built by scripts/demos/build.ts.
import { DEMO_FAMILIES, demoFamilyOf, EXERCISES, recordedDemoProblem, type DemoClip, type Exercise, type RecordedDemo } from '@fitness/core';
import { familyPropsFor, propsLabel, type FamilyProps, type FamilyPropsInput } from './familyProps';
import { IMPORTED, type ImportedDemo } from './imported';
import { thumbName } from './thumbName';
import THUMBS from './thumbs.json';

export interface DemoAsset {
  /** Clip id, the same as the file name in public/demos/clips/. */
  id: string;
  /** Where the motion comes from. */
  source: string;
  license: string;
  /** Hip hinge or squat depth matters: the session adds a small side view. */
  sideView: boolean;
  /** A kettlebell moves with the hands. */
  kettlebell: boolean;
  /** Recorded demos carry their motion inline (no file to fetch). */
  clip?: DemoClip;
  /** Imported real motion (src/demo/imported.ts): the credit line the exercise page shows. */
  credit?: ImportedDemo['credit'];
  /** A family motion standing in for the exercise's own demo ("Similar movement"). */
  similar?: boolean;
  /** Equipment in the hands and playback settings (family motions). */
  props?: FamilyProps;
  /** One-arm exercise on a two-arm clip: the left arm stays where it starts. */
  restLeftArm?: boolean;
}

const OG3D = 'Mesh2Motion clip (CC0), retargeted to the MakeHuman figure by OpenGym3D (MIT)';
export const OWN_LICENSE = 'Own work, all rights reserved';
const OWN = 'Our own keyframed motion on the same figure (procedural, scripts/demos/families.ts)';

/** The shared human: MakeHuman / MPFB2 figure (CC0) as exported by OpenGym3D (MIT), quantized by our build. */
export const HUMAN = { file: 'human.glb', source: 'MakeHuman figure from OpenGym3D', license: 'CC0-1.0 (figure), MIT (OpenGym3D export)' };

export const DEMOS: Record<string, DemoAsset> = {
  push_up: { id: 'push_up', source: OWN, license: OWN_LICENSE, sideView: false, kettlebell: false },
  jumping_jack: { id: 'jumping_jack', source: OG3D, license: 'CC0-1.0', sideView: false, kettlebell: false },
  jog: { id: 'jog', source: OG3D, license: 'CC0-1.0', sideView: false, kettlebell: false },
  bear_crawl: { id: 'bear_crawl', source: OG3D, license: 'CC0-1.0', sideView: false, kettlebell: false },
  kettlebell_swing: { id: 'kettlebell_swing', source: OWN, license: OWN_LICENSE, sideView: true, kettlebell: true },
  kettlebell_deadlift: { id: 'kettlebell_deadlift', source: OWN, license: OWN_LICENSE, sideView: true, kettlebell: true },
  squat: { id: 'squat', source: OWN, license: OWN_LICENSE, sideView: true, kettlebell: false },
  single_leg_squat: { id: 'single_leg_squat', source: OWN, license: OWN_LICENSE, sideView: false, kettlebell: false },
  plank: { id: 'plank', source: OWN, license: OWN_LICENSE, sideView: false, kettlebell: false },
};

/** Real motion from licensed sources (CMU motion capture, MM-Fit and Commons videos), by exercise id. */
const IMPORTED_BY_EXERCISE = new Map<string, ImportedDemo>();
for (const d of IMPORTED) for (const e of d.exercises) IMPORTED_BY_EXERCISE.set(e, d);
const CATALOG_BY_ID = new Map(EXERCISES.map((e) => [e.id, e]));
const importedAssets = new Map<string, DemoAsset>();

/**
 * The imported demo for an exercise id (or the exercise it was copied from), or null. Like a family motion it shows the
 * exercise's equipment in the hands (familyPropsFor: the family's hold, demoProps per exercise); a clip with its own
 * kettlebell track keeps that bell. One asset object per clip and equipment, so the viewer is not rebuilt on every render.
 */
export function importedFor(e: Partial<FamilyPropsInput>): DemoAsset | null {
  const mapped = e.id && IMPORTED_BY_EXERCISE.has(e.id) ? e.id : e.copiedFrom && IMPORTED_BY_EXERCISE.has(e.copiedFrom) ? e.copiedFrom : null;
  if (!mapped) return null;
  const d = IMPORTED_BY_EXERCISE.get(mapped)!;
  // Equipment from the exercise itself when it carries it (a member's copy may change it), else from the catalog entry.
  const source = e.equipment ? (e as FamilyPropsInput) : CATALOG_BY_ID.get(mapped);
  const p = d.kettlebell || !source ? null : familyPropsFor(source);
  const props = p?.kind ? { ...p, oneArm: false, tempo: 1 } : undefined;
  const key = `${d.id}|${props?.kind}|${props?.hold}|${props?.hangDown}`;
  let a = importedAssets.get(key);
  if (!a) {
    a = { id: d.id, source: d.credit.text, license: d.license, sideView: d.sideView, kettlebell: d.kettlebell, credit: d.credit, ...(props ? { props } : {}) };
    importedAssets.set(key, a);
  }
  return a;
}

/** Old OpenGym3D ids that saved exercises may still carry. */
const ALIASES: Record<string, string> = { pistol_squat: 'single_leg_squat' };

export const RECORDING_SOURCE = 'Own recording, made on the device from the camera\'s body points (no video kept)';

/** One asset object per recorded demo, so the viewer is not rebuilt on every render. */
const recorded = new WeakMap<RecordedDemo, DemoAsset | null>();

/**
 * A recorded demo is another member's data: it is checked fully (frame count, rate, tracks, every value) before the
 * viewer sees it, once per object. A malformed one shows the text fallback, never a frozen or broken page.
 */
function recordedAsset(d: RecordedDemo): DemoAsset | null {
  if (recorded.has(d)) return recorded.get(d)!;
  const a = recordedDemoProblem(d) === null
    ? { id: `recording-${d.id}`, source: RECORDING_SOURCE, license: d.license, sideView: d.sideView, kettlebell: d.clip.kettlebell === true, clip: d.clip }
    : null;
  recorded.set(d, a);
  return a;
}

/**
 * The demo for an exercise, or null when there is none we can show (then the page shows a text instead). Order: the
 * exercise's own recorded demo (a malformed one: null, the text fallback), then imported real motion for its id (or the
 * exercise it was copied from), then the built-in clip it names. shownDemoFor adds the family motion after these.
 */
export function demoFor(e: (Pick<Exercise, 'demo'> & Partial<Pick<Exercise, 'id' | 'copiedFrom'>>) | undefined | null): DemoAsset | null {
  if (!e) return null;
  const demo = e.demo;
  // A member's recorded demo comes first; a malformed one shows the text fallback (it is never hidden behind another clip).
  if (demo && typeof demo === 'object' && demo.source === 'recording') return recordedAsset(demo);
  const imported = importedFor(e);
  if (imported) return imported;
  if (!demo || typeof demo !== 'object') return null;
  if (typeof demo.id !== 'string' || !demo.id) return null;
  const id = demo.id;
  return DEMOS[ALIASES[id] ?? id] ?? null;
}

const FAMILY_SOURCE = 'Our own keyframed family motion on the same figure (procedural, scripts/demos/families.ts)';
const families = new Map<string, DemoAsset>();

/**
 * The family motion for an exercise without its own demo: the family's clip with the exercise's equipment, labeled
 * "Similar movement". One asset object per clip and settings, so the viewer is not rebuilt on every render.
 */
export function familyDemoFor(e: FamilyPropsInput): DemoAsset | null {
  const id = demoFamilyOf(e);
  if (!id) return null;
  const family = DEMO_FAMILIES[id];
  const props = familyPropsFor(e);
  // One-arm exercises use the family's one-arm clip when it has one; otherwise the left arm rests where it starts.
  const clip = props.oneArm && family.oneArmClip ? family.oneArmClip : family.clip;
  const restLeftArm = props.oneArm && !family.oneArmClip;
  const key = `${clip}|${props.kind}|${props.hold}|${restLeftArm}|${props.tempo}|${props.hangDown}|${family.sideView}`;
  let a = families.get(key);
  if (!a) {
    const own = DEMOS[clip];
    a = { id: clip, source: own?.source ?? FAMILY_SOURCE, license: own?.license ?? OWN_LICENSE, sideView: family.sideView, kettlebell: own?.kettlebell ?? false, similar: true, props, ...(restLeftArm ? { restLeftArm } : {}) };
    families.set(key, a);
  }
  return a;
}

export type ShownInput = Pick<Exercise, 'demo'> & Partial<FamilyPropsInput>;

/**
 * What the figure shows for an exercise: its own demo (built-in, free or recorded) when it has one, else its family's
 * motion. Null without an exercise, or for exercises no family motion shows well (they keep "No 3D demo yet").
 */
export function shownDemoFor(e: ShownInput | undefined | null): DemoAsset | null {
  if (!e) return null;
  const own = demoFor(e);
  if (own) return own;
  if (!e.id || !e.equipment) return null;
  return familyDemoFor(e as FamilyPropsInput);
}

/** "Similar movement", plus the equipment it shows ("Similar movement with dumbbells"). */
export function similarLabel(a: DemoAsset): string {
  const props = a.props ? propsLabel(a.props) : '';
  return props ? `Similar movement ${props}` : 'Similar movement';
}

const base = () => (import.meta.env?.BASE_URL ?? '/').replace(/\/?$/, '/');

export const humanUrl = () => `${base()}demos/${HUMAN.file}`;
export const clipUrl = (d: DemoAsset) => `${base()}demos/clips/${d.id}.json`;
const THUMB_NAMES = new Set<string>(THUMBS);

/**
 * Still picture for lists: the clip's most telling frame with the exercise's equipment (rendered from the same figure
 * at 3x the largest size the lists show, `THUMBS=1 ... -g thumbs`); the plain clip picture when that equipment has none.
 */
export function thumbUrl(d: DemoAsset): string {
  const p = d.props;
  const name = thumbName({ clip: d.id, kind: p?.kind ?? null, hold: p?.hold ?? null, oneArm: !!p?.oneArm, down: !!p?.hangDown });
  return `${base()}demos/thumbs/${THUMB_NAMES.has(name) ? name : d.id}.webp`;
}
/** Recorded demos have no list picture (yet): lists show the text fallback. */
export const hasThumb = (d: DemoAsset) => !d.clip;
